/**
 * An event log's spec: the concept-level facts every event log states once.
 *
 * The route's schema, the request prologue, the client's query type and the client's coverage key are all derived
 * from it. A spec is a value, not a base class. Everything a log does with its rows (its read, its DTO, its page, its
 * landing in the client store) stays the log's own; the spec only names what the shared window, coverage, lens and
 * shape machinery needs to know.
 */

import type { LensRecord, LensValues } from "./lens";
import { assertParamNames, assertParamNamesFree } from "./params";
import type { EventLogLimits } from "./query";
import { EVENT_LOG_SHARED_PARAMS } from "./query";
import type { ShapeRecord } from "./shape";

export type EventLogSpec<TName extends string, TLenses extends LensRecord, TShape extends ShapeRecord> = {
  /** The log's identity: the coverage log name and the telemetry prefix, such as `feedItems`. */
  name: TName;
  limits: EventLogLimits;
  /**
   * The instant history mode orders on. Delta mode is always `createdAt`.
   *
   * Read by the route's query schema, which documents what `until` bounds in history mode. The read itself orders on
   * the column its repository picks; `EventLogKey.historyAt` reads the same instant off a served row.
   */
  historyAxis: "createdAt" | "occurredAt";
  /** Which rows a read serves; see `lens.ts`. */
  lenses: TLenses;
  /** How much of each row a read serves; see `shape.ts`. Empty for a log whose rows have one shape. */
  shape: TShape;
  /** Whether a read with these lens values needs a `since` at most `EVENT_LOG_MAX_AGE_DAYS` back. */
  floored: (lenses: LensValues<TLenses>) => boolean;
};

/**
 * Declares a spec, keeping its name as a literal so the coverage key and the telemetry prefix stay typed.
 *
 * The lenses and the shape parameters are keyed by name and each states its name, so the two are checked against
 * each other once, at declaration. Every name must be unique across the shared query, the lenses and the shape, since
 * the route reads them all from one query string.
 *
 * @param spec - The log's concept-level facts.
 * @throws {Error} when a lens or a shape parameter sits under a key other than its name, or is named like a shared
 *   parameter or like a parameter of the other record.
 */
export function defineEventLog<const TName extends string, TLenses extends LensRecord, TShape extends ShapeRecord>(
  spec: EventLogSpec<TName, TLenses, TShape>,
): EventLogSpec<TName, TLenses, TShape> {
  assertParamNames(spec.name, "lens", spec.lenses);
  assertParamNames(spec.name, "shape parameter", spec.shape);
  const taken = new Map<string, string>(EVENT_LOG_SHARED_PARAMS.map((name) => [name, "a shared query parameter"]));
  assertParamNamesFree(spec.name, "lens", spec.lenses, taken);
  for (const name of Object.keys(spec.lenses)) {
    taken.set(name, "a lens");
  }
  assertParamNamesFree(spec.name, "shape parameter", spec.shape, taken);
  return spec;
}

/**
 * Any spec, for code that reads the shared facts without caring which log they belong to.
 */
export type AnyEventLogSpec = EventLogSpec<string, LensRecord, ShapeRecord>;
